import {TimelineConfiguration} from "../timeline/timeline-configuration.interface";
import {RteValueHelper} from "./rte-value.helper";

/**
 * Deliberately free of any import that touches the DOM. `TransformerHelper`
 * imports this class for `toKebabCase` alone, so anything pulled in here is
 * pulled into the whole command→DTO serialisation path — the original reason
 * `getQuillText` was split out into its own module (`QuillTextHelper`,
 * removed in the RTE unification, 2026-09-14, once no caller needed a live
 * Quill instance to read plain text any more — see `RteValueHelper.plainText`).
 * `RteValueHelper` itself stays safe to import here: quill-free, no DOM.
 */
export class StringHelper {

    public static toKebabCase(str: string): string {
        return str.replace(
            /[A-Z]+(?![a-z])|[A-Z]/g,
            (subStr, ofs) => (ofs ? `-` : ``) + subStr.toLowerCase()
        ).replace(/[ ,;.]+/g, `-`).replace(/--+/g, '-');
    }

    public static isFilledString(value: any): boolean {
        return typeof value === 'string' && value.length > 0;
    }

    /**
     * `true` iff `value` is a non-empty rich-text field — either shape of
     * `RteValue` (`Delta | RichTextDocument`, see its own doc comment). Shape
     * detection delegates to `RteValueHelper` (Review-Lauf 02, MINOR, Linse
     * B/C: this used to re-implement the same `Array.isArray(value?.ops)` /
     * `value?.type === 'doc'` checks independently, risking the two silently
     * diverging) — the actual "non-empty" check stays this method's OWN,
     * deliberately loose one, NOT `RteValueHelper.hasContent`'s stricter
     * "has real text/content" definition: a legacy Quill Delta counts as
     * non-empty from any op at all (matches Quill's own looseness), and a
     * migrated `RichTextDocument` counts as non-empty from any content node
     * at all (an empty single paragraph — `RteValueHelper.empty()`'s own
     * shape — still reads as "no content" via `content.length > 0` failing
     * for it, since `content` there is `[]`... except `RteValueHelper.empty()`
     * actually has ONE paragraph node, so this DOES count it as non-empty —
     * a known, unchanged looseness carried forward from before this
     * delegation, not introduced by it). The name predates the RTE
     * unification and stayed — every caller already reads it as "is there
     * something to show here", which is still exactly what it answers.
     */
    public static hasQuillContent(value: any): boolean {
        if (RteValueHelper.isQuillDelta(value)) {
            return value.ops.length > 0;
        }
        if (RteValueHelper.isRichTextDocument(value)) {
            return Array.isArray(value.content) && value.content.length > 0;
        }
        return false;
    }

    public static toSnakeCase(str: string): string {
        const result = str.replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/[\s-]+/g, '_');

        return (result.startsWith('_'))
            ? result.substring(1)
            : result;
    }

    public static toLowerCamelCase(str: string): string {
        return str
            // Zuerst wird der String in Wörter aufgeteilt
            .split(/[-_]/)
            // Jedes Wort nach dem ersten wird mit einem Großbuchstaben am Anfang versehen
            .map((word, index) =>
                index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            // Die transformierten Wörter werden wieder zu einem String zusammengefügt
            .join('');
    }

    public static buildUserViewConfigurationKey(configuration: TimelineConfiguration): string {
        return `${configuration.baseEntityAlias ?? StringHelper.toKebabCase(configuration.baseEntity.name)}:${configuration.pathToEntity}`;
    }
}